import React, { useEffect, useState } from 'react';
import { X, Globe } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../i18n';

// Smart language-switcher hint:
// If the user's browser languages suggest a different supported locale than the
// one currently shown, surface a small dismissible toast offering to switch.
// Persists "dismissed" in localStorage so we never nag again.

const SUPPORTED = ['en', 'me', 'sq', 'de', 'ru', 'pl', 'hu'];
const STORAGE_KEY = 'hotel-langhint-dismissed';
// Don't surface the toast for crawlers — they already see hreflang.
const BOT_UA_PATTERN = /(bot|crawl|spider|GPTBot|ChatGPT|Claude|Perplexity|Google-Extended|OAI-SearchBot|Applebot|Bytespider|Amazonbot|Yandex|Baidu|Slurp|DuckDuck)/i;

const browserPreferredLocale = () => {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const l of langs || []) {
    if (!l) continue;
    const code = l.toLowerCase().split('-')[0];
    const mapped = code === 'sr' ? 'me' : code;
    if (SUPPORTED.includes(mapped)) return mapped;
  }
  return null;
};

const stripLangPrefix = (path) => {
  const segs = (path || '/').split('/');
  if (segs.length > 1 && SUPPORTED.includes(segs[1])) segs.splice(1, 1);
  return segs.join('/') || '/';
};

const LanguageHint = () => {
  const { lang, setLang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Skip on admin / booking confirmation
    if (location.pathname.startsWith('/admin')) return;
    if (location.pathname.startsWith('/booking-confirmed')) return;
    if (location.pathname.includes('/terrace/') || location.pathname.includes('/inroom/')) return;  // QR pages pick their own language

    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch {
      // localStorage may throw in private mode / when disabled — treat as "not dismissed"
    }

    if (typeof navigator !== 'undefined' && BOT_UA_PATTERN.test(navigator.userAgent || '')) return;

    const pref = browserPreferredLocale();
    if (!pref) return;
    if (pref === lang) return;

    // Show after a short delay so it doesn't fight initial paint.
    const timer = setTimeout(() => {
      setTarget(pref);
      setVisible(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [lang, location.pathname]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage may throw in private mode / when disabled — toast just won't be remembered
    }
    setVisible(false);
  };

  const switchTo = () => {
    if (!target) return;
    setLang(target);
    const cleanPath = stripLangPrefix(location.pathname);
    const prefix = target === 'en' ? '' : `/${target}`;
    const tail = cleanPath === '/' ? '/' : cleanPath;
    navigate(`${prefix}${tail}${location.search}${location.hash}`, { replace: false });
    dismiss();
  };

  if (!visible || !target) return null;

  // Show the message in the SUGGESTED language (so the user can read it).
  const message = getTranslation(target, 'langHint.message');
  const switchLabel = getTranslation(target, 'langHint.switch');
  const dismissLabel = getTranslation(target, 'langHint.dismiss');

  return (
    <div
      className="fixed bottom-6 left-6 right-6 sm:left-6 sm:right-auto sm:max-w-md z-[60] animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="dialog"
      aria-live="polite"
      data-testid="language-hint"
    >
      <div className="bg-white shadow-2xl rounded-2xl border border-orange-200 p-4 flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
          <Globe className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 leading-snug">{message}</p>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={switchTo}
              className="px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full transition"
              data-testid="language-hint-switch"
            >
              {switchLabel} →
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium"
              data-testid="language-hint-dismiss"
            >
              {dismissLabel}
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded"
          aria-label={dismissLabel}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default LanguageHint;
